// W40 — Nyomtatható projektjelentés.
//
// A jelentés ugyanaz az egy DOM-fa képernyőn és papíron: nincs külön
// export-sablon, amit karban kellene tartani. A különbséget egyetlen
// `@media print` blokk adja, amit a modul egyszer beszúr a `<head>`-be:
// navigáció és gombok eltűnnek, a papír fehér, a szöveg fekete.
//
// A csatolt terv (W40) PDF/Excel/CSV választót is mutat; a tényleges
// exportot a böngésző nyomtatás-párbeszéde adja (PDF-be nyomtatás), ezért itt
// egyetlen, valóban működő út van: `window.print()`.

import { t } from '../lib/i18n';
import { statusBadge } from '../lib/components';
import { h, icon } from '../lib/dom';
import type { PersonView, ProjectView } from '../lib/data';
import { formatElapsed, statusLabel, type TaskView } from '../lib/model';
import { completedCount, nextDeadline } from './projects';

// ---------------------------------------------------------------------------
// Nyomtatási szabályok
// ---------------------------------------------------------------------------

const printStyleId = 'blaze-print-styles';

/// Csak nyomtatásra érvényes szabályok. Szándékosan egyetlen `@media print`
/// blokk: képernyőn semmit nem változtat meg, így nem tud elrontani más
/// képernyőt.
const printCss = `
@media print {
  @page { size: A4 portrait; margin: 14mm; }

  html, body { background: #FFFFFF !important; color: #000000 !important; }

  /* A görgethető dobozok papíron elvágnák a jelentést — kinyitjuk őket. */
  #root, .app, .shell, .shell-main, .shell-body, .shell-content, .shell-detail {
    display: block !important;
    overflow: visible !important;
    height: auto !important;
    max-height: none !important;
    background: #FFFFFF !important;
  }

  /* Navigáció, fejlécsáv, lebegő rétegek és minden gomb: papíron nincs értelmük. */
  .sidebar, .bottomnav, .topbar, .tabs, .toast-host, .dialog-overlay, .scrim,
  .print-hide, button {
    display: none !important;
  }

  .print-scope, .print-sheet {
    display: block !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    width: auto !important;
    max-width: none !important;
    background: #FFFFFF !important;
  }

  /* Papíron minden szín feketére vált: a státuszt az ikon és a szöveg hordozza,
     nem a háttérszín — így a fekete-fehér nyomat is olvasható. */
  .print-sheet, .print-sheet * {
    color: #000000 !important;
    background: transparent !important;
    box-shadow: none !important;
    border-color: #000000 !important;
  }

  .print-head { border-bottom: 2px solid #000000 !important; padding-bottom: 12px !important; }
  .print-foot { border-top: 1px solid #000000 !important; padding-top: 10px !important; }

  .print-table { width: 100% !important; border-collapse: collapse !important; }
  .print-table th, .print-table td {
    border-bottom: 1px solid #000000 !important;
    padding: 6px 8px !important;
    text-align: left !important;
    vertical-align: top !important;
  }
  /* Többoldalas jelentésnél minden oldal kapjon fejlécet, és ne törjön sor. */
  .print-table thead { display: table-header-group !important; }
  .print-table tfoot { display: table-footer-group !important; }
  .print-table tr { break-inside: avoid !important; page-break-inside: avoid !important; }

  a[href]::after { content: '' !important; }
}
`;

/// A nyomtatási szabályok beszúrása — idempotens, többszöri hívásra is egy
/// `<style>` elem marad a dokumentumban.
export function ensurePrintStyles(): void {
  if (document.getElementById(printStyleId)) return;
  const style = h('style', { id: printStyleId, type: 'text/css', text: printCss });
  document.head.append(style);
}

// ---------------------------------------------------------------------------
// Összesítő
// ---------------------------------------------------------------------------

export type ReportStats = {
  total: number;
  done: number;
  overdue: number;
  elapsedSeconds: number;
  deadline?: Date;
};

/// A fejléc és a táblázat lábának közös számai — tiszta függvény.
export function reportStats(project: ProjectView): ReportStats {
  return {
    total: project.tasks.length,
    done: completedCount(project),
    overdue: project.tasks.filter((task) => task.isOverdue).length,
    elapsedSeconds: project.tasks.reduce((sum, task) => sum + task.elapsedSeconds, 0),
    deadline: nextDeadline(project),
  };
}

/// Dátum idő nélkül — a jelentés fejlécében és láblécében ez az alak szerepel.
export function formatReportDate(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}. ${pad(date.getMonth() + 1)}. ${pad(date.getDate())}.`;
}

// ---------------------------------------------------------------------------
// W40 — a jelentés
// ---------------------------------------------------------------------------

const checkPath = 'M3.6 8.4l2.8 2.8 6-6.4';

const printerIconPath =
  'M4.4 6V2.6h7.2V6M2.4 6h11.2v4.6H2.4zM4.4 9.4h7.2v4H4.4z';

/// A papír színei fixek: a jelentés akkor is fehér lapon fekete, ha az app
/// sötét témában fut.
const paperInk = '#1A1815';
const paperMuted = '#56504A';
const paperLine = '#E2DCD1';

export type PrintableProjectOptions = {
  project: ProjectView;
  /// A felelősök feloldásához; ami nincs benne, az „Ismeretlen".
  people?: PersonView[];
  /// A fejléc jobb oldalán megjelenő cégnév.
  companyName?: string;
  /// Lábléc bal oldala.
  footerNote?: string;
  /// A jelentés kelte; tesztelhetőség miatt felülírható.
  now?: Date;
  /// A Nyomtatás gomb viselkedésének felülírása. Alapértelmezésben
  /// `window.print()` fut.
  onPrint?: () => void;
  /// CSV letöltése a jelentés adataiból.
  onExportCsv?: (csv: string, fileName: string) => void;
  onBack?: () => void;
};

/// A jelentés tartalmi kapcsolói (W40 „Tartalom").
type PrintSections = {
  tasks: boolean;
  time: boolean;
  timeline: boolean;
  comments: boolean;
  signatures: boolean;
  fields: boolean;
};

/// A jelentés CSV-je: soronként egy feladat.
///
/// Pontosvessző az elválasztó és vessző a tizedesjel — a magyar Excel ezt
/// nyitja meg oszlophelyesen, ugyanúgy, mint a riportok exportját.
function projectCsv(
  project: ProjectView,
  people: PersonView[],
  sections: PrintSections,
): string {
  const cell = (value: string | number) => {
    const text = String(value);
    return /[";\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
  };

  const header = [
    t('print.colSeq'),
    t('report.csvTask'),
    t('task.stepAssignee'),
    t('task.status'),
    t('general.deadline'),
  ];
  if (sections.time) header.push(t('print.colTracked'));
  if (sections.fields) header.push(t('print.colFields'));

  const lines = [header];

  for (const task of project.tasks) {
    const row = [
      task.sequenceNumber !== undefined ? String(task.sequenceNumber) : '',
      task.title,
      task.assigneeIds
        .map((id) => people.find((person) => person.id === id)?.fullName ?? 'Ismeretlen')
        .join(', '),
      statusLabel(task.status),
      task.deadline ? formatReportDate(task.deadline) : '',
    ];
    if (sections.time) {
      row.push((task.elapsedSeconds / 3600).toFixed(2).replace('.', ','));
    }
    if (sections.fields) {
      row.push(
        task.fields
          .filter((field) => !field.isType && field.value)
          .map((field) => `${field.name}: ${field.value}`)
          .join(' | '),
      );
    }
    lines.push(row);
  }

  return lines.map((line) => line.map(cell).join(';')).join('\r\n');
}

/// Egy projekt teljes feladatlánca nyomtatásra optimalizált elrendezésben.
export function printableProject(options: PrintableProjectOptions): HTMLElement {
  ensurePrintStyles();

  const { project } = options;
  const now = options.now ?? new Date();
  const people = options.people ?? [];
  const stats = reportStats(project);
  const company = options.companyName ?? 'Blaze';

  function assigneeNames(task: TaskView): string {
    const names = task.assigneeIds.map(
      (id) => people.find((person) => person.id === id)?.fullName ?? 'Ismeretlen',
    );
    return names.length > 0 ? names.join(', ') : '—';
  }

  function stat(label: string, value: string): HTMLElement {
    return h(
      'div',
      { class: 'col', style: { gap: '2px', minWidth: '0' } },
      h('span', {
        style: {
          fontSize: '11px',
          letterSpacing: '.05em',
          textTransform: 'uppercase',
          fontWeight: '700',
          color: paperMuted,
        },
        text: label,
      }),
      h('span', { style: { fontSize: '15px', fontWeight: '600' }, text: value }),
    );
  }

  const headCellStyle = {
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    color: paperMuted,
    textAlign: 'left',
    padding: '7px 8px',
    borderBottom: `1.5px solid ${paperInk}`,
  } as const;

  const cellStyle = {
    fontSize: '13px',
    padding: '8px',
    borderBottom: `1px solid ${paperLine}`,
    verticalAlign: 'top',
  } as const;

  const columns: { label: string; width?: string }[] = [
    { label: t('print.colSeq'), width: '70px' },
    { label: t('report.csvTask') },
    { label: t('task.stepAssignee'), width: '150px' },
    { label: t('task.status'), width: '130px' },
    { label: t('general.deadline'), width: '110px' },
    { label: t('report.colTracked'), width: '90px' },
  ];

  const table = h(
    'table',
    { class: 'print-table', style: { width: '100%', borderCollapse: 'collapse' } },
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        ...columns.map((column) =>
          h('th', {
            scope: 'col',
            style: { ...headCellStyle, ...(column.width ? { width: column.width } : {}) },
            text: column.label,
          }),
        ),
      ),
    ),
    h(
      'tbody',
      {},
      ...(project.tasks.length === 0
        ? [
            h(
              'tr',
              {},
              h('td', {
                colspan: String(columns.length),
                style: { ...cellStyle, color: paperMuted },
                text: 'Ebben a projektben nincs feladat.',
              }),
            ),
          ]
        : project.tasks.map((task) =>
            h(
              'tr',
              {},
              h('td', {
                class: 'mono',
                style: cellStyle,
                text: task.sequenceNumber !== undefined ? `${task.sequenceNumber}.` : '—',
              }),
              h(
                'td',
                { style: cellStyle },
                h('span', { style: { fontWeight: '600' }, text: task.title }),
              ),
              h('td', { style: cellStyle, text: assigneeNames(task) }),
              h('td', { style: cellStyle }, statusBadge(task.status)),
              h('td', {
                style: cellStyle,
                text: task.deadline ? formatReportDate(task.deadline) : '—',
              }),
              h('td', {
                class: 'mono',
                style: cellStyle,
                text: task.elapsedSeconds > 0 ? formatElapsed(task.elapsedSeconds) : '—',
              }),
            ),
          )),
    ),
    // Összesítő: a táblázat lába, hogy többoldalas nyomatnál is a helyén legyen.
    h(
      'tfoot',
      {},
      h(
        'tr',
        {},
        h('td', {
          colspan: '3',
          style: { ...cellStyle, fontWeight: '700', borderBottom: '0' },
          text: t('print.totalTasks', [String(stats.total)]),
        }),
        h('td', {
          colspan: '2',
          style: { ...cellStyle, fontWeight: '700', borderBottom: '0' },
          text: t('print.doneOverdue', [String(stats.done), String(stats.overdue)]),
        }),
        h('td', {
          class: 'mono',
          style: { ...cellStyle, fontWeight: '700', borderBottom: '0' },
          text: formatElapsed(stats.elapsedSeconds),
        }),
      ),
    ),
  );

  // -------------------------------------------------------------------------
  // W40 — export-beallitasok
  // -------------------------------------------------------------------------

  const sections: PrintSections = {
    tasks: true,
    time: true,
    timeline: false,
    comments: false,
    signatures: true,
    fields: true,
  };
  let orientation: 'portrait' | 'landscape' = 'portrait';
  let paper: 'A4' | 'Letter' = 'A4';
  let headerText = options.companyName
    ? t('print.headerWithCompany', [options.companyName])
    : '';

  const extrasHost = h('div', { class: 'col g18', style: { gap: '18px' } });
  const headTitle = h('h1', { style: { fontSize: '20px', fontWeight: '700' }, text: t('print.reportTitle') });

  /// A papírméret és a tájolás a nyomtatáskor a `@page` szabályból jön. Ezt
  /// egyetlen, névvel ellátott stíluselem hordozza, hogy a választás
  /// újrarajzolás nélkül is érvényes legyen.
  const pageStyle = h('style', { id: 'blaze-print-page' }) as HTMLStyleElement;
  function applyPageRule(): void {
    pageStyle.textContent = `@media print { @page { size: ${paper} ${orientation}; } }`;
  }
  applyPageRule();

  /// Az idővonal, a megjegyzések, az aláírások és az egyedi mezők a feladatok
  /// ALATT, külön szakaszban jelennek meg — a táblázat így olvasható marad.
  function renderExtras(): void {
    const blocks: HTMLElement[] = [];

    function block(title: string, ...rows: (HTMLElement | null)[]): HTMLElement {
      return h(
        'section',
        { class: 'col g6', style: { breakInside: 'avoid' } },
        h('h2', {
          style: {
            fontSize: '11px',
            letterSpacing: '.05em',
            textTransform: 'uppercase',
            fontWeight: '700',
            color: paperMuted,
          },
          text: title,
        }),
        ...rows.filter((r): r is HTMLElement => r !== null),
      );
    }

    if (sections.timeline) {
      const rows = project.tasks
        .filter((task) => task.statusChangeDesc || task.failedDesc || task.reopens.length > 0)
        .map((task) =>
          h('p', {
            style: { fontSize: '13px' },
            text: `${task.sequenceNumber ?? '—'}. ${task.title} — ${[
              task.statusChangeDesc,
              task.failedDesc,
              ...task.reopens.map((r) => r.reason),
            ]
              .filter(Boolean)
              .join(' · ')}`,
          }),
        );
      blocks.push(
        block(
          t('print.timelineAndReasons'),
          ...(rows.length > 0
            ? rows
            : [h('p', { style: { fontSize: '13px', color: paperMuted }, text: t('print.noReason') })]),
        ),
      );
    }

    if (sections.comments) {
      const rows = project.tasks.flatMap((task) =>
        task.comments.map((comment) =>
          h('p', {
            style: { fontSize: '13px' },
            text: `${task.sequenceNumber ?? '—'}. ${task.title} — ${comment.text}`,
          }),
        ),
      );
      blocks.push(
        block(
          t('report.comments'),
          ...(rows.length > 0
            ? rows
            : [h('p', { style: { fontSize: '13px', color: paperMuted }, text: t('print.noComment') })]),
        ),
      );
    }

    if (sections.signatures) {
      const signed = project.tasks.filter((task) => task.signatureUrl);
      if (signed.length > 0) {
        blocks.push(
          block(
            t('print.signatures'),
            ...signed.map((task) =>
              h(
                'div',
                { class: 'col g4', style: { breakInside: 'avoid' } },
                h('span', { style: { fontSize: '13px' }, text: task.title }),
                h('img', {
                  src: task.signatureUrl!,
                  alt: t('print.signatureOf', [task.title]),
                  style: { maxWidth: '260px', border: `1px solid ${paperLine}` },
                }),
              ),
            ),
          ),
        );
      }
    }

    if (sections.fields) {
      const rows = project.tasks.flatMap((task) =>
        task.fields
          .filter((field) => !field.isType && field.value)
          .map((field) =>
            h('p', {
              style: { fontSize: '13px' },
              text: `${task.sequenceNumber ?? '—'}. ${task.title} — ${field.name}: ${field.value}`,
            }),
          ),
      );
      if (rows.length > 0) blocks.push(block(t('task.customFields'), ...rows));
    }

    extrasHost.replaceChildren(...blocks);
  }
  renderExtras();

  function checkRow(label: string, key: keyof PrintSections): HTMLElement {
    const box = h(
      'span',
      { class: `cb${sections[key] ? ' on' : ''}`, 'aria-hidden': 'true' },
      sections[key]
        ? h('span', { class: 'ic', style: { width: '13px', height: '13px' } }, icon(checkPath, 13))
        : null,
    );

    return h(
      'label',
      {
        class: 'row g10',
        style: { alignItems: 'center', padding: '5px 0', cursor: 'pointer', position: 'relative' },
      },
      h('input', {
        type: 'checkbox',
        checked: sections[key] ? 'checked' : undefined,
        style: { position: 'absolute', opacity: '0', width: '20px', height: '20px', margin: '0' },
        onChange: (event: Event) => {
          sections[key] = (event.target as HTMLInputElement).checked;
          box.className = `cb${sections[key] ? ' on' : ''}`;
          box.replaceChildren(
            sections[key]
              ? h('span', { class: 'ic', style: { width: '13px', height: '13px' } }, icon(checkPath, 13))
              : h('span'),
          );
          if (key === 'tasks') table.style.display = sections.tasks ? '' : 'none';
          renderExtras();
        },
      }),
      box,
      h('span', { class: 'body-sm', text: label }),
    );
  }

  function seg<T extends string>(
    values: { key: T; label: string }[],
    current: () => T,
    onPick: (value: T) => void,
  ): HTMLElement {
    const host = h('div', { class: 'seg', style: { alignSelf: 'flex-start' } });
    function draw(): void {
      host.replaceChildren(
        ...values.map((value) =>
          h('button', {
            type: 'button',
            class: value.key === current() ? 'on' : '',
            'aria-pressed': value.key === current() ? 'true' : 'false',
            text: value.label,
            onClick: () => {
              onPick(value.key);
              draw();
            },
          }),
        ),
      );
    }
    draw();
    return host;
  }

  const optionsPanel = h(
    'aside',
    {
      class: 'panel pad20 col g16 print-hide',
      style: { width: '360px', flex: 'none', gap: '16px' },
    },
    h('span', { class: 'h3', text: t('print.exportSettings') }),
    h(
      'div',
      { class: 'field' },
      h('span', { class: 'flbl', text: t('print.format') }),
      h(
        'div',
        { class: 'row g8 wrap' },
        h(
          'button',
          {
            class: 'btn btn-primary btn-sm',
            type: 'button',
            onClick: () => {
              if (options.onPrint) options.onPrint();
              else window.print();
            },
          },
          h('span', { class: 'ic' }, icon(printerIconPath, 14)),
          h('span', { text: t('print.pdfPrint') }),
        ),
        options.onExportCsv
          ? h('button', {
              class: 'btn btn-secondary btn-sm',
              type: 'button',
              text: t('print.downloadCsv'),
              onClick: () =>
                options.onExportCsv?.(
                  projectCsv(project, people, sections),
                  `${project.title.replace(/[^\w.-]+/g, '_')}.csv`,
                ),
            })
          : null,
      ),
      // A terv „Excel" formatumot is kinal. Valodi .xlsx-hez kulon konyvtar
      // kellene; a CSV-t a magyar Excel oszlophelyesen nyitja meg, ezert az
      // all itt helyette.
      h('span', { class: 'hint', text: 'Az Excel a CSV-t oszlophelyesen nyitja meg.' }),
    ),
    h(
      'div',
      { class: 'field' },
      h('span', { class: 'flbl', text: 'Tartalom' }),
      checkRow(t('print.sectionTasks'), 'tasks'),
      checkRow(t('print.sectionTime'), 'time'),
      checkRow(t('print.timelineAndReasons'), 'timeline'),
      checkRow(t('report.comments'), 'comments'),
      checkRow(t('print.sectionSignatures'), 'signatures'),
      checkRow(t('report.contentFields'), 'fields'),
    ),
    h(
      'div',
      { class: 'field' },
      h('span', { class: 'flbl', text: t('print.paperAndOrientation') }),
      h(
        'div',
        { class: 'row g8' },
        seg(
          [
            { key: 'A4' as const, label: 'A4' },
            { key: 'Letter' as const, label: 'Letter' },
          ],
          () => paper,
          (value) => {
            paper = value;
            applyPageRule();
          },
        ),
        seg(
          [
            { key: 'portrait' as const, label: t('print.portrait') },
            { key: 'landscape' as const, label: t('print.landscape') },
          ],
          () => orientation,
          (value) => {
            orientation = value;
            applyPageRule();
          },
        ),
      ),
    ),
    h(
      'div',
      { class: 'field' },
      h('label', { class: 'flbl', for: 'print-head-text', text: t('print.headerTextLabel') }),
      h('input', {
        class: 'input',
        id: 'print-head-text',
        type: 'text',
        value: headerText,
        placeholder: t('print.reportTitle'),
        onInput: (event: Event) => {
          headerText = (event.target as HTMLInputElement).value;
          headTitle.textContent = headerText.trim() || t('print.reportTitle');
        },
      }),
    ),
    h('span', {
      class: 'caption',
      text: t('print.printHint'),
    }),
    pageStyle,
  );

  const sheet = h(
    'section',
    {
      class: 'print-sheet',
      'aria-label': t('print.documentLabel', [project.title]),
      style: {
        background: '#FFFFFF',
        color: paperInk,
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        boxShadow: 'var(--e2)',
        padding: '40px',
        maxWidth: '860px',
        width: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
      },
    },
    h(
      'header',
      {
        class: 'print-head row between top g16',
        style: { borderBottom: `2px solid ${paperInk}`, paddingBottom: '14px' },
      },
      h(
        'div',
        { class: 'col', style: { gap: '4px', minWidth: '0' } },
        headTitle,
        h('p', { style: { fontSize: '13px', color: paperMuted }, text: project.title }),
      ),
      h(
        'div',
        { class: 'col', style: { gap: '2px', alignItems: 'flex-end', flex: 'none' } },
        h('span', { style: { fontSize: '13px', fontWeight: '600' }, text: company }),
        h('span', { style: { fontSize: '12px', color: paperMuted }, text: formatReportDate(now) }),
      ),
    ),
    h(
      'div',
      { class: 'row g32 wrap' },
      stat('Haladás', `${stats.done} / ${stats.total}`),
      stat('Mért idő', formatElapsed(stats.elapsedSeconds)),
      stat('Lejárt', String(stats.overdue)),
      stat('Határidő', stats.deadline ? formatReportDate(stats.deadline) : '—'),
    ),
    project.description
      ? h('p', { style: { fontSize: '13px', color: paperMuted }, text: project.description })
      : null,
    table,
    extrasHost,
    h(
      'footer',
      {
        class: 'print-foot row between g12',
        style: {
          marginTop: 'auto',
          borderTop: `1px solid ${paperLine}`,
          paddingTop: '10px',
          fontSize: '11px',
          color: paperMuted,
        },
      },
      h('span', { text: options.footerNote ?? `${company} · Blaze` }),
      h('span', { text: `Nyomtatva: ${formatReportDate(now)}` }),
    ),
  );

  return h(
    'div',
    { class: 'print-scope col g16', style: { padding: '24px' } },
    h(
      'div',
      { class: 'print-hide row between g12 wrap' },
      h(
        'div',
        { class: 'row g12', style: { minWidth: '0' } },
        options.onBack
          ? h('button', {
              class: 'btn btn-ghost btn-icon btn-sm',
              type: 'button',
              'aria-label': 'Vissza a projekthez',
              text: '‹',
              onClick: options.onBack,
            })
          : null,
        h(
          'div',
          { class: 'col', style: { gap: '2px', minWidth: '0' } },
          h('h2', { class: 'h3', text: t('print.exportTitle') }),
          h('span', {
            class: 'caption',
            text: t('print.printHint'),
          }),
        ),
      ),
      h(
        'button',
        {
          class: 'btn btn-primary',
          type: 'button',
          onClick: () => {
            if (options.onPrint) options.onPrint();
            else window.print();
          },
        },
        h('span', { class: 'ic' }, icon(printerIconPath, 15)),
        h('span', { text: t('project.print') }),
      ),
    ),
    // A terv ketoszlopos: balra a papir, jobbra az export-beallitasok. A
    // panel nyomtataskor eltunik (`print-hide`).
    h(
      'div',
      { class: 'row g24 wrap', style: { alignItems: 'flex-start', gap: '24px' } },
      h('div', { class: 'grow', style: { minWidth: '0', flexBasis: '520px' } }, sheet),
      optionsPanel,
    ),
  );
}
